import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { isNil, noop } from 'lodash-es'
import {
  AmountFormat,
  AuthorisedUser,
  Collateral,
  DealActions,
  DealDetail,
  DealServicingStartSubstituteArgs,
  DealStatus,
  EntityType,
  NegotiationFieldMeta,
  ProductSector,
  RepoType,
  StockBorrowingLendingType,
  TradingType,
  UserPreference,
  ValidationIssue
} from 'api'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import {
  BooleanSelectboxField,
  OrganisationDecisionMakerSelectboxField,
  SubstitutableCollateralComboboxField,
  TradingDatePickerField
} from '../entity'
import { TextButton, TextButtonType } from 'view/components/buttons'
import {
  canServicingAccept,
  canServicingCancel,
  canServicingRevertAttributes,
  canServicingSend,
  getDealModificationColour,
  getNegotiatedValue
} from 'view/routes/deals/components/utils'
import {
  canEditMidTradeEventSubstitutionDetailStartConsiderationOverride,
  getFeeFormatOptions,
  getHaircutFormatOptions,
  getNotionalFormatOptions,
  getRateFormatOptions,
  getStartConsiderationFormatOptions
} from '../datagrid'
import { createInvalidValueValidationIssue, createMissingMandatoryValidationIssue, getDateEditorFormat, Severity } from 'model'
import { createHandleStartConsiderationOverrideUpdate } from './utils'
import { DateLike, notNilFilter, Nullable, toLocalDateString } from 'utils'
import { createDealDetailSelector } from 'selectors/dealSelectors'
import { platformTradeDateSelector } from 'selectors/platformSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { Locale, Translation } from 'locale'
import {
  getBondPriceModificationType,
  getHaircutModificationType,
  getNotionalModificationType,
  getRateModificationType,
  getStartConsiderationModificationType,
  getStartConsiderationOverrideModificationType,
  getStartDateModificationType,
  getStringModificationType
} from 'view/routes/requests/shared/deltaGetters'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import { ModalConsumerProps, NotificationType, withModal } from '../modal'
import moment from 'moment'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { HaircutTypeSelectboxField } from '../entity/haircutOptions/HaircutTypeSelectbox'
import { bondPriceMaxFieldLength, bondPriceMaxFractionDigits } from 'view/styles/constants'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { swallowPrecisionError } from 'utils/Decimal'
import { ServicingSecondaryCollateralComboboxField } from '../entity/SecondaryCollateralSelectbox'
import { useOnMount } from 'view/hooks'
import { useGetEffectiveDateInfo } from './useGetEffectiveDateInfo'
import { authorisedUserSelector } from 'selectors/connectionSelectors'
import { ValidationIssuesContainer } from '../error'
import { FieldsContainer } from './servicingDialogStyles'

type NegotiationFieldReadOnly = { [P in NegotiationFieldMeta]?: boolean }

export type ServicingSubstitutionDialogContainerProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  dealId: string
  onClose?: () => unknown
  isAdmin?: boolean
  readOnly?(context: { newCollateral: Nullable<Collateral> }): NegotiationFieldReadOnly
}

interface StateProps {
  dealDetail: DealDetail | undefined
  platformTradeDate: DateLike | null
  userPreference: UserPreference
  getCollateral: (collateralId: string) => Collateral | null
  startConsiderationOverrideEnabled: boolean
  getProductSector: (productSectorId: string) => ProductSector | null
  stockBorrowingLendingEnabled: boolean
  user: AuthorisedUser
}

interface DispatchProps {
  onServicingStartSubstitute: (args: DealServicingStartSubstituteArgs) => unknown
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onRevert: typeof DealActions.servicingRevertAttributes
  onSend: typeof DealActions.servicingSend
  onCancel: typeof DealActions.servicingCancel
}

export interface ServicingSubstitutionDialogComponentProps
  extends ModalConsumerProps,
    Omit<ServicingSubstitutionDialogContainerProps, 'onSend'> {
  canSend: (dealDetail: DealDetail) => boolean
  titleLabel: Translation
  confirmBtnLabel: Translation
  onSend: (dealId: string) => void
}

export const ServicingSubstitutionDialogComponent: React.FC<ServicingSubstitutionDialogComponentProps> = ({
  isAdmin,
  user,
  canSend,
  confirmBtnLabel,
  dealId,
  dealDetail,
  platformTradeDate,
  titleLabel,
  userPreference,
  getCollateral,
  onClose = noop,
  onServicingStartSubstitute,
  onUpdateField,
  onSend,
  onCancel,
  startConsiderationOverrideEnabled,
  getProductSector,
  stockBorrowingLendingEnabled,
  readOnly: _readOnly = () => ({} as NegotiationFieldReadOnly)
}) => {
  const getEffectiveDateInfo = useGetEffectiveDateInfo(true)

  useOnMount(() => {
    if (dealDetail?.deal.status !== DealStatus.SERVICING_WITH_INITIATOR) {
      onServicingStartSubstitute({ dealId })
    }
  })

  if (!dealDetail) return null

  const { deal, servicingSideDetails, collateral, referenceRate, ownOrder } = dealDetail

  const negotiationFields = servicingSideDetails?.negotiationFields
  const sideFields = servicingSideDetails?.sideFields

  const newIsin = getNegotiatedValue(negotiationFields?.COLLATERAL?.negotiableFieldRequired)
  const collateralModificationType = getStringModificationType(negotiationFields?.COLLATERAL?.negotiableFieldRequired)
  const newCollateral = isNil(newIsin) ? null : getCollateral(newIsin)
  const productSector = isNil(newCollateral) ? null : getProductSector(newCollateral.productSectorId)
  const isStockBorrowingLending = deal.repoType === RepoType.STOCK_BORROWING_LENDING

  const [defaultEffectiveDate, minEffectiveDate, maxEffectiveDate, isBeforeCutOffTime] = getEffectiveDateInfo(dealDetail, newCollateral)

  const effectiveDate =
    getNegotiatedValue(servicingSideDetails?.negotiationFields[NegotiationFieldMeta.START_DATE]?.negotiableFieldRequired) ??
    toLocalDateString(defaultEffectiveDate)

  const clientValidationIssues: ValidationIssue[] = [
    isNil(newCollateral) ? createMissingMandatoryValidationIssue('collateralId') : null,
    collateralModificationType
      ? null
      : createInvalidValueValidationIssue('collateralId', Locale.translate(Translation.SC_COLLATERAL_UNCHANGED), Severity.ERROR),
    platformTradeDate && effectiveDate && moment(effectiveDate).isSame(platformTradeDate, 'day') && !isBeforeCutOffTime
      ? createInvalidValueValidationIssue('startDate', Locale.translate(Translation.SC_EFFECTIVE_DATE_TODAY_WARNING_MESSAGE), Severity.WARN)
      : null
  ].filter(notNilFilter)

  const validationIssues = (servicingSideDetails?.validationIssues ?? []).concat(clientValidationIssues)
  const hasValidationErrors = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const notionalFormatOptions = getNotionalFormatOptions(userPreference.amountFormat)
  const startConsiderationFormatOptions = getStartConsiderationFormatOptions()
  const haircutFormatOptions = getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, deal.direction)

  const handleStartConsiderationOverrideUpdate = createHandleStartConsiderationOverrideUpdate(userPreference, dealId, onUpdateField)

  const handleUpdateField = (value: any, field: NegotiationFieldMeta, rawValue?: any) => {
    if (field === NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE) {
      onUpdateField({
        dealId,
        field,
        updatedValue: value,
        updatedRawValue: rawValue,
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
      onUpdateField({
        dealId,
        field: NegotiationFieldMeta.START_CONSIDERATION,
        updatedValue: null,
        updatedRawValue: '',
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
    } else if (field === NegotiationFieldMeta.COLLATERAL) {
      onUpdateField({
        dealId,
        field,
        updatedValue: value,
        updatedRawValue: rawValue,
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
      onUpdateField({
        dealId,
        field: NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE,
        updatedValue: false,
        updatedRawValue: false,
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
    } else {
      onUpdateField({
        dealId,
        field,
        updatedValue: value,
        updatedRawValue: rawValue,
        decisionMakerOrientedByNegotiationSide: true,
        meta: { haircutSign: userPreference.haircutSign },
        revertFirst: false
      })
    }
  }

  const handleSend = () => onSend(dealId)

  const handleCancel = async () => {
    if (canServicingCancel(dealDetail)) {
      onCancel(dealDetail.deal.dealId)
    }
    onClose()
  }

  const readOnly = _readOnly({ newCollateral })
  const rosWarning = dealDetail.deal.rightOfSubstitution === 0

  return (
    <ModalWindow title={Locale.translate(titleLabel)} onEscKey={handleCancel}>
      <ModalContent>
        {rosWarning && (
          <FieldsContainer>
            <ValidationIssuesContainer>{<li>{Locale.translate(Translation.SC_REQUEST_HAS_NO_ROS)}</li>}</ValidationIssuesContainer>
          </FieldsContainer>
        )}
        <Groupbox>
          <DialogRow>
            <DialogField flex={7}>
              <TextInputField
                label={Locale.translate(Translation.TC_CURRENT_COLLATERAL)}
                value={collateral?.name}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField label={Locale.translate(Translation.TC_FACTOR)} value={deal.factor} readOnly={true} onUpdate={noop} />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_CURRENT_HAIRCUT)}
                formatOptions={haircutFormatOptions}
                value={deal.haircut}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={5}>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_CURRENT_HAIRCUT_TYPE)}
                value={deal.haircutType}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_CURRENT_RATE)}
                formatOptions={getRateFormatOptions(referenceRate)}
                value={deal.rate}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
          {stockBorrowingLendingEnabled ? (
            <DialogRow>
              <DialogField flex={5}>
                <NumericInputField
                  label={Locale.translate(Translation.TC_FEE)}
                  value={deal.fee}
                  readOnly={true}
                  onUpdate={noop}
                  formatOptions={getFeeFormatOptions()}
                />
              </DialogField>
              <DialogField flex={5}>
                <TextInputField
                  label={Locale.translate(Translation.TC_SECONDARY_COLLATERAL)}
                  value={dealDetail.secondaryInstrumentName}
                  readOnly={true}
                  onUpdate={noop}
                />
              </DialogField>
              <DialogField flex={5}>
                <TextInputField
                  label={Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE)}
                  value={deal.stockBorrowingLendingType}
                  readOnly={true}
                  onUpdate={noop}
                />
              </DialogField>
            </DialogRow>
          ) : null}
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                formatOptions={notionalFormatOptions}
                value={deal.notional}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                formatOptions={startConsiderationFormatOptions}
                value={deal.startConsideration}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
            {startConsiderationOverrideEnabled && (
              <DialogField>
                <BooleanSelectboxField
                  label={Locale.translate(Translation.TC_START_CONSIDERATION_OVERRIDE)}
                  value={deal.startConsiderationOverride}
                  readOnly={true}
                  onUpdate={noop}
                />
              </DialogField>
            )}
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
                value={deal.bondPrice}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
        <Groupbox>
          <DialogRow>
            <DialogField flex={7}>
              <SubstitutableCollateralComboboxField
                label={Locale.translate(Translation.TC_NEW_COLLATERAL)}
                fieldName={NegotiationFieldMeta.COLLATERAL}
                validationField={DealFieldNames.DEAL_COLLATERAL_ISIN}
                productId={deal.productId}
                marketType={deal.marketType}
                currency={deal.currency}
                fundId={deal.fundId}
                counterpartyId={deal.counterpartyId}
                parentDealBasketId={deal.parentDealBasketId}
                originalIsin={deal.collateralId!}
                repoType={deal.repoType}
                validationIssues={validationIssues}
                underline={newIsin === null ? null : getDealModificationColour(() => collateralModificationType, dealDetail)}
                value={collateralModificationType ? newIsin : null}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingSubstitutionDialog-SubstitutableCollateralComboboxField'}
              />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_FACTOR)}
                fieldName={NegotiationFieldMeta.FACTOR}
                value={getNegotiatedValue(negotiationFields?.FACTOR?.negotiableFieldRequired)}
                onUpdate={noop}
                readOnly={true}
                dataTestId={'ServicingSubstitutionDialog-NewFactorNumericInputField'}
              />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_HAIRCUT)}
                fieldName={NegotiationFieldMeta.HAIRCUT}
                validationField={DealFieldNames.DEAL_HAIRCUT}
                formatOptions={haircutFormatOptions}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getHaircutModificationType(negotiationFields?.HAIRCUT?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.HAIRCUT?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                readOnly={
                  isNil(newCollateral) || (deal.stockBorrowingLendingType === StockBorrowingLendingType.BASKET && isStockBorrowingLending)
                }
                dataTestId={'ServicingSubstitutionDialog-NewHaircutNumericInputField'}
              />
            </DialogField>
            <DialogField flex={5}>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_NEW_HAIRCUT_TYPE)}
                fieldName={NegotiationFieldMeta.HAIRCUT_TYPE}
                validationField={DealFieldNames.DEAL_HAIRCUT_TYPE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStringModificationType(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                readOnly={
                  isNil(newCollateral) || (deal.stockBorrowingLendingType === StockBorrowingLendingType.BASKET && isStockBorrowingLending)
                }
                dataTestId={'ServicingSubstitutionDialog-HaircutTypeSelectboxField'}
              />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_RATE)}
                fieldName={NegotiationFieldMeta.RATE}
                validationField={DealFieldNames.DEAL_RATE}
                formatOptions={getRateFormatOptions(referenceRate)}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getRateModificationType(negotiationFields?.RATE?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.RATE?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                readOnly={isNil(newCollateral) || isStockBorrowingLending}
                dataTestId={'ServicingSubstitutionDialog-NewRateNumericInputField'}
              />
            </DialogField>
          </DialogRow>
          {stockBorrowingLendingEnabled ? (
            <DialogRow>
              <DialogField>
                <NumericInputField
                  label={Locale.translate(Translation.TC_NEW_FEE)}
                  fieldName={NegotiationFieldMeta.FEE}
                  validationField={DealFieldNames.DEAL_FEE}
                  formatOptions={getFeeFormatOptions()}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getRateModificationType(negotiationFields?.FEE?.negotiableFieldRequired),
                    dealDetail
                  )}
                  value={getNegotiatedValue(negotiationFields?.FEE?.negotiableFieldRequired)}
                  onUpdate={handleUpdateField}
                  dataTestId={'ServicingSubstitutionDialog-NewFeeNumericInputField'}
                  readOnly={!isStockBorrowingLending}
                />
              </DialogField>
              <DialogField>
                <ServicingSecondaryCollateralComboboxField
                  detail={dealDetail}
                  label={Locale.translate(Translation.TC_NEW_SECONDARY_COLLATERAL)}
                  fieldName={NegotiationFieldMeta.SECONDARY_BASKET_ID}
                  validationField={DealFieldNames.DEAL_SECONDARY_COLLATERAL}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.SECONDARY_BASKET_ID?.negotiableFieldRequired),
                    dealDetail
                  )}
                  value={getNegotiatedValue(negotiationFields?.SECONDARY_BASKET_ID?.negotiableFieldRequired)}
                  onUpdate={handleUpdateField}
                  dataTestId={'ServicingSubstitutionDialog-NewSecondaryCollateralComboboxField'}
                  readOnly={!isStockBorrowingLending}
                />
              </DialogField>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_NEW_STOCK_BORROW_LENDING_TYPE)}
                  value={deal.stockBorrowingLendingType}
                  readOnly={true}
                  onUpdate={noop}
                />
              </DialogField>
            </DialogRow>
          ) : null}
          <DialogRow>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                fieldName={NegotiationFieldMeta.NOTIONAL}
                validationField={DealFieldNames.DEAL_NOTIONAL}
                formatOptions={notionalFormatOptions}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                isInteger={userPreference.amountFormat === AmountFormat.ACTUAL}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getNotionalModificationType(negotiationFields?.NOTIONAL?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.NOTIONAL?.negotiableFieldRequired)}
                readOnly={newCollateral?.tradingType === TradingType.CASH}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingSubstitutionDialog-NewNotionalNumericInputField'}
              />
            </DialogField>
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_START_CONSIDERATION)}
                fieldName={NegotiationFieldMeta.START_CONSIDERATION}
                validationField={DealFieldNames.DEAL_START_CONSIDERATION}
                formatOptions={startConsiderationFormatOptions}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                validationIssues={validationIssues}
                underline={swallowPrecisionError(getDealModificationColour)(
                  () => getStartConsiderationModificationType(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired)}
                readOnly={newCollateral?.tradingType === TradingType.NOTIONAL}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingSubstitutionDialog-NewStartConsiderationNumericInputField'}
              />
            </DialogField>
            {startConsiderationOverrideEnabled && (
              <DialogField flex={5}>
                <BooleanSelectboxField
                  label={Locale.translate(Translation.TC_NEW_START_CONSIDERATION_OVERRIDE)}
                  fieldName={NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE}
                  validationField={DealFieldNames.DEAL_START_CONSIDERATION_OVERRIDE}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getStartConsiderationOverrideModificationType(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired),
                    dealDetail
                  )}
                  value={getNegotiatedValue(negotiationFields?.START_CONSIDERATION_OVERRIDE?.negotiableFieldRequired)}
                  readOnly={canEditMidTradeEventSubstitutionDetailStartConsiderationOverride(dealDetail, newCollateral, productSector)}
                  onUpdate={handleStartConsiderationOverrideUpdate}
                  updateOnlyOnChangedValue={true}
                  dataTestId={'ServicingSubstitutionDialog-NewStartConsiderationNumericInputField'}
                />
              </DialogField>
            )}
            <DialogField flex={5}>
              <NumericInputField
                label={Locale.translate(Translation.TC_NEW_COLLATERAL_PRICE)}
                fieldName={NegotiationFieldMeta.BOND_PRICE}
                validationField={DealFieldNames.DEAL_BOND_PRICE}
                min={0}
                maxLength={bondPriceMaxFieldLength}
                formatOptions={{
                  maxFractionDigits: bondPriceMaxFractionDigits
                }}
                validationIssues={validationIssues}
                underline={swallowPrecisionError(getDealModificationColour)(
                  () => getBondPriceModificationType(negotiationFields?.BOND_PRICE?.negotiableFieldRequired),
                  dealDetail
                )}
                value={getNegotiatedValue(negotiationFields?.BOND_PRICE?.negotiableFieldRequired)}
                onUpdate={handleUpdateField}
                dataTestId={'ServicingSubstitutionDialog-NewCollateralPriceNumericInputField'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TradingDatePickerField
                label={Locale.translate(Translation.TC_EFFECTIVE_DATE)}
                fieldName={NegotiationFieldMeta.START_DATE}
                currency={collateral?.currency}
                minDate={minEffectiveDate ?? undefined}
                maxDate={maxEffectiveDate ?? undefined}
                dateFormat={getDateEditorFormat(userPreference.dateFormat)}
                validationField={DealFieldNames.DEAL_START_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStartDateModificationType(negotiationFields?.START_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                value={effectiveDate}
                readOnly={isNil(newCollateral)}
                onUpdate={(value: string | null) => handleUpdateField(value, NegotiationFieldMeta.START_DATE)}
                dataTestId={'ServicingSubstitutionDialog-EffectiveDateTradingDatePickerField'}
              />
            </DialogField>
            <DialogField>
              <OrganisationDecisionMakerSelectboxField
                organisationId={isAdmin ? dealDetail.deal.initiatorOrganisationId : user.organisationId}
                label={Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER)}
                fieldName={
                  ownOrder
                    ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
                    : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER
                }
                validationField={
                  ownOrder
                    ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                    : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
                }
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () =>
                    getStringModificationType(
                      sideFields?.[ownOrder ? 'INITIATOR_INVESTMENT_DECISION_MAKER' : 'RESPONDER_INVESTMENT_DECISION_MAKER']
                        ?.negotiableFieldRequired
                    ),
                  dealDetail
                )}
                value={getNegotiatedValue(
                  sideFields?.[ownOrder ? 'INITIATOR_INVESTMENT_DECISION_MAKER' : 'RESPONDER_INVESTMENT_DECISION_MAKER']
                    ?.negotiableFieldRequired
                )}
                onUpdate={handleUpdateField}
                disabled={ownOrder ? readOnly.INITIATOR_INVESTMENT_DECISION_MAKER : readOnly.RESPONDER_INVESTMENT_DECISION_MAKER}
                dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Investment'}
              />
            </DialogField>
            <DialogField>
              <OrganisationDecisionMakerSelectboxField
                organisationId={isAdmin ? dealDetail.deal.initiatorOrganisationId : user.organisationId}
                label={Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER)}
                fieldName={
                  ownOrder
                    ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
                    : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER
                }
                validationField={
                  ownOrder ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
                }
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () =>
                    getStringModificationType(
                      sideFields?.[ownOrder ? 'INITIATOR_EXECUTION_DECISION_MAKER' : 'RESPONDER_EXECUTION_DECISION_MAKER']
                        ?.negotiableFieldRequired
                    ),
                  dealDetail
                )}
                value={getNegotiatedValue(
                  sideFields?.[ownOrder ? 'INITIATOR_EXECUTION_DECISION_MAKER' : 'RESPONDER_EXECUTION_DECISION_MAKER']
                    ?.negotiableFieldRequired
                )}
                onUpdate={handleUpdateField}
                disabled={ownOrder ? readOnly.INITIATOR_EXECUTION_DECISION_MAKER : readOnly.RESPONDER_EXECUTION_DECISION_MAKER}
                dataTestId={'ServicingSubstitutionDialog-DecisionMakerSelectboxField-Execution'}
              />
            </DialogField>
          </DialogRow>
        </Groupbox>
      </ModalContent>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel} dataTestId={'ServicingDialog-CancelButton'}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              disabled={hasValidationErrors || !canServicingRevertAttributes(dealDetail) || !canSend(dealDetail)}
              onClick={handleSend}
              dataTestId={'ServicingSubstitutionDialog-SendRequestButton'}>
              {Locale.translate(confirmBtnLabel)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

const dealIdSelector = (_: {}, props: ServicingSubstitutionDialogContainerProps) => props.dealId

const mapStateToProps = createStructuredSelector({
  dealDetail: createDealDetailSelector(dealIdSelector),
  platformTradeDate: platformTradeDateSelector,
  userPreference: createUserPreferenceSelector(),
  getCollateral: createEntityGetterSelector(EntityType.COLLATERAL),
  startConsiderationOverrideEnabled: startConsiderationOverrideEnabledSelector,
  getProductSector: createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  stockBorrowingLendingEnabled: isStockBorrowingLendingEnabledSelector,
  user: authorisedUserSelector
})

const mapDispatchToProps = {
  onServicingStartSubstitute: DealActions.servicingStartSubstitute,
  onUpdateField: DealActions.servicingUpdateAttributes,
  onRevert: DealActions.servicingRevertAttributes,
  onCancel: DealActions.servicingCancel,
  onSend: (dealId: string) => DealActions.servicingSend(dealId)
}

export const ServicingSubstitutionDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(
    mapStateToProps,
    mapDispatchToProps
  )((props: ServicingSubstitutionDialogContainerProps) => {
    const { onSend, onClose = noop } = props
    return (
      <ServicingSubstitutionDialogComponent
        {...props}
        canSend={canServicingSend}
        isAdmin={false}
        titleLabel={Translation.TC_SUBSTITUTION}
        confirmBtnLabel={Translation.TC_BUTTON_SEND_REQUEST}
        onSend={dealId => {
          onSend(dealId)
          onClose()
        }}
        readOnly={({ newCollateral }) => ({
          [NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER]: isNil(newCollateral),
          [NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER]: isNil(newCollateral),
          [NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER]: isNil(newCollateral),
          [NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER]: isNil(newCollateral)
        })}
      />
    )
  })
)

export const ServicingAdminSubstitutionDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, {
    ...mapDispatchToProps,
    onServicingStartSubstitute: DealActions.servicingStartAdminSubstitute,
    onSend: DealActions.servicingAccept
  })((props: ServicingSubstitutionDialogContainerProps) => {
    const { onSend, onClose = noop, showDialog } = props
    return (
      <ServicingSubstitutionDialogComponent
        {...props}
        canSend={canServicingAccept}
        isAdmin={true}
        titleLabel={Translation.TC_SUBSTITUTION}
        confirmBtnLabel={Translation.TC_SUBSTITUTE}
        onSend={async dealId => {
          if (
            (await showDialog({
              buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
              type: NotificationType.Confirm,
              title: Translation.TC_SUBSTITUTE_TRADE,
              messageText: Locale.translate(Translation.SC_SUBSTITUTE_TRADE_CONFIRMATION)
            })) === Translation.TC_BUTTON_YES
          ) {
            onSend(dealId)
            onClose()
          }
        }}
        readOnly={({ newCollateral }) => ({
          [NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER]: isNil(newCollateral),
          [NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER]: isNil(newCollateral),
          [NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER]: isNil(newCollateral),
          [NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER]: isNil(newCollateral)
        })}
      />
    )
  })
)
